import React, { useState, useEffect } from "react";
import BoxComponent from "../Components/Box";
import Head from "../Components/Head";
import Sidebar from "../Components/Sidebar";
import FiguresBox from "../Components/FiguresBox";
import TypographyComponent from "../Components/Typography";
import MapComponent from "../Components/MapComponent";
import { getDashboardData } from "../api/constants";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useDebounce } from "../hooks/useDebounce";

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);

  // Default location for the map center (can be updated based on admin's location)
  const [userLocation, setUserLocation] = useState({
    latitude: 33.6366351,
    longitude: 73.1052798,
  });

  // Map center state that updates immediately for smooth UX
  const [mapCenter, setMapCenter] = useState([33.6366351, 73.1052798]);

  // Debounce the user location for API calls to avoid too many requests
  const debouncedUserLocation = useDebounce(userLocation, 1000); // 1 second delay
  useEffect(() => {
    // Get user's location if available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setUserLocation(newLocation);
          setMapCenter([newLocation.latitude, newLocation.longitude]);
        },
        (error) => {
          console.log("Geolocation error:", error);
          // Use default location if geolocation fails
        }
      );
    }
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = Cookies.get("token");

        const params = {
          latitude: debouncedUserLocation.latitude,
          longitude: debouncedUserLocation.longitude,
        };

        const response = await getDashboardData(params, token);

        if (response?.data?.success) {
          const data = response.data.data;
          setDashboardData(data);

          // Transform driver data for map
          const transformedDrivers = data.drivers.map((driver, index) => ({
            id: driver._id,
            name: `${driver.firstName} ${driver.lastName}`,
            position: [driver.latitude, driver.longitude],
            status: "Active", // All drivers in this API response are active
            city: `Distance: ${driver.distance.toFixed(2)} km`,
            profileImage: driver.profileImageURL,
          }));

          setDrivers(transformedDrivers);
        } else {
          toast.error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Error fetching dashboard data");
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchData();

    // Set up polling interval to refresh data every 30 seconds
    const pollInterval = setInterval(() => {
      fetchData();
    }, 30000); // 30 seconds

    // Cleanup interval on component unmount or dependency change
    return () => {
      clearInterval(pollInterval);
    };
  }, [debouncedUserLocation]);
  // Function to handle map center changes (when user drags the map)
  const handleMapCenterChange = async (newCenter) => {
    try {
      // Update map center immediately for smooth UX
      setMapCenter([newCenter.latitude, newCenter.longitude]);
      // Update user location (will be debounced for API calls)
      setUserLocation(newCenter);
    } catch (error) {
      console.error("Error updating map center:", error);
    }
  };

  const mapZoom = 10;

  return (
    <BoxComponent backgroundColor="var(--light)">
      <Head />
      <BoxComponent display="flex" justifyContent="space-between">
        <Sidebar />
        <BoxComponent
          width="82%"
          sx={{ padding: "20px", overflowY: "auto", maxHeight: "85vh" }}
        >
          <TypographyComponent
            fontSize="18px"
            fontFamily="var(--main)"
            color="var(--dark)"
            fontWeight="400"
          >
            DASHBOARD
          </TypographyComponent>
          <FiguresBox dashboardData={dashboardData} loading={loading} />
          <BoxComponent
            display="flex"
            flexDirection="column"
            marginTop="15px"
            overflow="hidden"
            backgroundColor="var(--white)"
          >
            {" "}
            <TypographyComponent
              fontSize="16px"
              color="var(--paragraph)"
              width="100%"
              padding="15px"
              backgroundColor="var(--white)"
              boxShadow="1px 1px 1px 1px var(--secondary)"
            >
              Location of the Drivers
            </TypographyComponent>
            <MapComponent
              cars={drivers}
              center={mapCenter}
              zoom={mapZoom}
              isDrawingAllowed={false}
              onCenterChange={handleMapCenterChange}
            />
          </BoxComponent>
        </BoxComponent>
      </BoxComponent>
    </BoxComponent>
  );
}
